/* eslint-disable no-return-assign */
import {
  Box,
  Button,
  Grid2,
  IconButton,
  List,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useAppContext } from "AppContext";
import { DocumentAddIcon, VaultIcon } from "components/Icons";
import { useAppDispatch } from "hooks/useToolkit";
import React, { useState } from "react";
import { AiOutlineQuestionCircle } from "react-icons/ai";
import { FiLogOut, FiTrendingUp, FiUsers } from "react-icons/fi";
import { HiDocument } from "react-icons/hi";
import { IoIosContact } from "react-icons/io";
import { IoMailOutline, IoSettingsOutline } from "react-icons/io5";
import { LuBuilding, LuLayoutDashboard } from "react-icons/lu";
import { PiHandshake } from "react-icons/pi";
import { RiAdminLine } from "react-icons/ri";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AuthenticationActions } from "services/redux/slices/Authentication";
import { resetStates } from "services/redux/store/revertStates";
import { MF_ONLY_PHONES } from "utils/mfOnlyUsers";

interface DashboardLayoutProps {
  title?: string;
  breadcrumbs?: { name: string; path: string }[];
  children?: React.ReactNode;
  toolbar?: React.ReactNode;
}

const DashboardLayout = ({ children, toolbar }: DashboardLayoutProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const {
    state: { currentUser },
  } = useAppContext();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    dispatch(resetStates());
    dispatch(AuthenticationActions.Logout());
    queryClient.clear();
    handleMenuClose();
  };

  const groups = [
    {
      name: "Dashboard",
      to: "/app/dashboard",
      icon: <LuLayoutDashboard size={18} />,
    },
    {
      name: "Users",
      to: "/app/dashboard/user-details",
      icon: <FiUsers size={18} />,
    },
    {
      name: "Admin Access Panel",
      to: "/app/admin-access-board",
      icon: <RiAdminLine size={18} />,
    },
    {
      name: "Company Master",
      to: "/app/company-master",
      icon: <LuBuilding size={18} />,
    },
    {
      name: "Broker Master",
      to: "/app/broker-master",
      icon: <PiHandshake size={18} />,
    },
    {
      name: "FAQs",
      to: "/app/card",
      icon: <AiOutlineQuestionCircle size={18} />,
    },
    {
      name: "Communication",
      to: "/app/communication",
      icon: <IoMailOutline size={18} />,
    },
    {
      name: "Internal",
      to: "/app/internal",
      icon: <IoSettingsOutline size={18} />,
    },
    {
      name: "Inquiry",
      to: "/app/inquiry",
      icon: <DocumentAddIcon />,
    },
    {
      name: "Vault Inquiries",
      to: "/app/vault-inquiries",
      icon: <VaultIcon />,
    },
    {
      name: "Leo Wealth",
      to: "/app/leo-wealth",
      icon: <HiDocument />,
    },
    {
      name: "MF",
      to: "/app/mf",
      icon: <FiTrendingUp size={18} />,
    },
  ];

  const isMFOnlyUser = MF_ONLY_PHONES.includes(currentUser?.phone);

  const filteredGroups = groups.filter((button) => {
    if (isMFOnlyUser) return button.name === "MF";
    return currentUser?.phone === "7016333333" ||
      currentUser?.phone === "9911223344"
      ? true
      : button.name !== "Company Master";
  });

  return (
    <Grid2 container height="100vh" width="100vw" wrap="nowrap">
      <Grid2
        sx={{
          flex: "0 0 235px",
          bgcolor: "#fff",
          borderRight: "1px solid var(--grey-100)",
          display: "flex",
          flexDirection: "column",
          overflowY: "auto",
        }}
      >
        <Box p={2} borderBottom={"solid 1px var(--neutral-100)"}>
          <Link to={isMFOnlyUser ? "/app/mf" : "/app/dashboard"}>
            <img
              src="/Logos/Gridkey_BrandGuidelines-removebg-preview.png"
              alt="Logo"
              style={{ height: 40 }}
            />
          </Link>
        </Box>
        <List sx={{ px: 2 }}>
          {filteredGroups.map((item) => {
            const isActive = location.pathname === item.to;
            return (
              <Button
                key={item.name}
                onClick={() => navigate(item.to)}
                fullWidth
                variant={isActive ? "lightBg" : "text"}
                sx={{
                  justifyContent: "flex-start",
                  textTransform: "none",
                  fontWeight: isActive ? 700 : 400,
                  px: 2,
                  py: 1.5,
                  fontSize: 14,
                  gap: 1.5,
                }}
              >
                {item.icon}
                {item.name}
              </Button>
            );
          })}
        </List>
      </Grid2>

      {/* Main Content */}
      <Grid2
        sx={{
          flex: "1 1 auto",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Top bar */}
        <Grid2
          sx={{
            flex: "0 0 78px",
            px: 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid var(--primary-100)",
            bgcolor: "var(--white)",
          }}
        >
          <Typography fontWeight={700} fontSize={14}>
            {filteredGroups.find((g) => location.pathname === g.to)?.name ??
              "Dashboard"}
          </Typography>

          <Box display="flex" alignItems="center" gap={1}>
            {toolbar}
            <IconButton onClick={handleMenuOpen}>
              <IoIosContact color="var(--primary-900)" />
            </IconButton>
          </Box>

          {/* Profile Menu */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            sx={{
              "& .MuiPaper-root": {
                backgroundColor: "rgba(255, 255, 255, 0.0)",
                backdropFilter: "blur(10px)",
                borderRadius: 2,
                boxShadow: "0px 14px 120px rgba(0, 0, 0, 0.1)",
                padding: 2,
                width: 320,
                minWidth: 250,
              },
            }}
          >
            <Grid2 container alignItems="center" spacing={1}>
              <Grid2>
                {currentUser?.profileImage ? (
                  <img
                    src={currentUser.profileImage}
                    alt="Profile"
                    style={{
                      height: 60,
                      width: 60,
                      borderRadius: "50%",
                      objectFit: "cover",
                      background: `var(--warning-700)11`,
                    }}
                  />
                ) : (
                  <IoIosContact size={50} color={"var(--primary-900)"} />
                )}
              </Grid2>
              <Grid2>
                <Typography
                  fontWeight={600}
                  fontSize={18}
                  color={"var(--primary-900)"}
                >
                  {currentUser?.phone ?? "No Phone Available"}
                </Typography>
              </Grid2>
            </Grid2>
            <MenuItem
              onClick={handleLogout}
              sx={{
                fontWeight: 500,
                fontSize: "16px",
                padding: "10px 2px",
                mt: 3,
                color: "var(--warning-700)",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <FiLogOut size={20} />
              Logout
            </MenuItem>
          </Menu>
        </Grid2>

        {/* Page Content */}
        <Grid2
          sx={{
            flex: "1 1 auto",
            p: 2,
            overflowY: "auto",
          }}
        >
          {children}
        </Grid2>
      </Grid2>
    </Grid2>
  );
};

export default DashboardLayout;
